import React, { useEffect, useState } from 'react';
import { Check, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import cuentaBancariaService from '../../services/cuentaBancaria.service';

const EMPTY_FORM = {
  nombre: '',
  beneficiario: '',
  banco: '',
  tipoCuenta: '',
  numeroCuenta: '',
  cbuAlias: '',
  detalles: '',
  activa: true,
  esDefault: false,
  orden: 0,
};

const errorMessage = (e, fallback) => e?.response?.data?.message || fallback;

function TextField({ label, value, onChange, placeholder, required }) {
  return (
    <div className='space-y-1'>
      <label className='block text-xs font-medium text-foreground'>
        {label}
        {required && ' *'}
      </label>
      <Input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

/**
 * Admin CRUD for bank accounts ("cuentas de destino"). Each reservation is linked to one account
 * so the club knows who receives the money of each sale.
 */
export default function CuentasBancariasConfig() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setCuentas(await cuentaBancariaService.getAll());
    } catch {
      toast.error('Error al cargar las cuentas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleEdit = c => {
    setForm({
      ...EMPTY_FORM,
      ...Object.fromEntries(Object.keys(EMPTY_FORM).map(k => [k, c[k] ?? EMPTY_FORM[k]])),
    });
    setEditingId(c._id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('El nombre es requerido');
    if (!form.beneficiario.trim()) return toast.error('El beneficiario es requerido');
    setSaving(true);
    try {
      if (editingId) {
        await cuentaBancariaService.update(editingId, form);
        toast.success('Cuenta actualizada');
      } else {
        await cuentaBancariaService.create(form);
        toast.success('Cuenta creada');
      }
      closeForm();
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async c => {
    try {
      await cuentaBancariaService.update(c._id, { activa: !c.activa });
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Error al actualizar'));
    }
  };

  const makeDefault = async c => {
    try {
      await cuentaBancariaService.setDefault(c._id);
      toast.success(`"${c.nombre}" es ahora la cuenta predeterminada`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Error al actualizar'));
    }
  };

  const handleDelete = async c => {
    if (c.reservasCount > 0) {
      return toast.error(
        'Esta cuenta tiene reservas asociadas y no puede eliminarse. Desactivala en su lugar.'
      );
    }
    if (!window.confirm(`¿Eliminar la cuenta "${c.nombre}"?`)) return;
    try {
      await cuentaBancariaService.remove(c._id);
      toast.success('Cuenta eliminada');
      load();
    } catch (e) {
      toast.error(errorMessage(e, 'Error al eliminar'));
    }
  };

  const activeCount = cuentas.filter(c => c.activa).length;

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <p className='text-sm text-muted-foreground'>
          Cuentas que reciben el dinero de cada venta. Cada reserva queda vinculada a una cuenta.
          Con 2 o más cuentas activas, el comprador elige el <strong>destino de la compra</strong>{' '}
          en el checkout (preseleccionada la predeterminada). Con una sola, se asigna
          automáticamente.
        </p>
        <Button
          size='sm'
          className='shrink-0'
          onClick={() => {
            if (showForm && !editingId) return closeForm();
            setForm(EMPTY_FORM);
            setEditingId(null);
            setShowForm(true);
          }}
        >
          <Plus className='mr-1.5 h-4 w-4' /> Nueva cuenta
        </Button>
      </div>

      {showForm && (
        <div className='space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm'>
          <p className='text-sm font-semibold text-foreground'>
            {editingId ? 'Editar cuenta' : 'Nueva cuenta'}
          </p>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <TextField
              label='Nombre (visible para el comprador)'
              required
              value={form.nombre}
              onChange={v => patch('nombre', v)}
              placeholder='Ej: Rugby'
            />
            <TextField
              label='Beneficiario'
              required
              value={form.beneficiario}
              onChange={v => patch('beneficiario', v)}
              placeholder='Ej: Tala Rugby Club'
            />
            <TextField
              label='Banco'
              value={form.banco}
              onChange={v => patch('banco', v)}
              placeholder='Ej: Banco Nación'
            />
            <TextField
              label='Tipo de cuenta'
              value={form.tipoCuenta}
              onChange={v => patch('tipoCuenta', v)}
              placeholder='Ej: Caja de ahorro, cuenta corriente'
            />
            <TextField
              label='Número de cuenta'
              value={form.numeroCuenta}
              onChange={v => patch('numeroCuenta', v)}
              placeholder='Ej: 0012345678'
            />
            <TextField
              label='CBU / CVU / Alias'
              value={form.cbuAlias}
              onChange={v => patch('cbuAlias', v)}
              placeholder='Ej: TALA.RUGBY.BONO'
            />
          </div>
          <div className='space-y-1'>
            <label className='block text-xs font-medium text-foreground'>Detalles / notas</label>
            <textarea
              value={form.detalles}
              onChange={e => patch('detalles', e.target.value)}
              rows={3}
              placeholder='Información adicional (uso interno)'
              className='w-full resize-y rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            />
            <p className='text-xs text-muted-foreground'>
              Los datos bancarios no se muestran públicamente: el comprador solo ve nombre y
              beneficiario.
            </p>
          </div>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
            <TextField
              label='Orden'
              value={String(form.orden)}
              onChange={v => patch('orden', Number(v) || 0)}
              placeholder='0'
            />
            <label className='flex items-center gap-2 text-sm font-medium text-foreground sm:pt-6'>
              <input
                type='checkbox'
                checked={form.activa}
                onChange={e => patch('activa', e.target.checked)}
                className='h-4 w-4 accent-primary'
              />
              Activa
            </label>
            <label className='flex items-center gap-2 text-sm font-medium text-foreground sm:pt-6'>
              <input
                type='checkbox'
                checked={form.esDefault}
                disabled={!form.activa}
                onChange={e => patch('esDefault', e.target.checked)}
                className='h-4 w-4 accent-primary'
              />
              Predeterminada
            </label>
          </div>
          <div className='flex gap-2 pt-1'>
            <Button onClick={handleSave} disabled={saving} size='sm'>
              <Check className='mr-1 h-4 w-4' />
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button variant='outline' size='sm' onClick={closeForm}>
              <X className='mr-1 h-4 w-4' /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className='text-sm text-muted-foreground'>Cargando…</p>
      ) : cuentas.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No hay cuentas configuradas. Las reservas se crean sin cuenta de destino.
        </p>
      ) : (
        <ul className='space-y-2'>
          {cuentas.map(c => (
            <li
              key={c._id}
              className='flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 shadow-sm sm:flex-row sm:items-center'
            >
              <div className='min-w-0 flex-1 space-y-1'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='text-sm font-semibold text-foreground'>{c.nombre}</span>
                  <span className='text-sm text-muted-foreground'>— {c.beneficiario}</span>
                  {c.esDefault && (
                    <span className='inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary'>
                      <Star className='h-2.5 w-2.5' /> Predeterminada
                    </span>
                  )}
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                      c.activa ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {c.activa ? 'Activa' : 'Inactiva'}
                  </span>
                  <span className='text-[11px] text-muted-foreground'>
                    {c.reservasCount || 0} reservas
                  </span>
                </div>
                {(c.banco || c.numeroCuenta || c.cbuAlias || c.detalles) && (
                  <p className='truncate text-xs text-muted-foreground'>
                    {[c.banco, c.tipoCuenta, c.numeroCuenta, c.cbuAlias, c.detalles]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
              <div className='flex shrink-0 items-center gap-1'>
                {!c.esDefault && c.activa && (
                  <Button variant='ghost' size='sm' onClick={() => makeDefault(c)}>
                    <Star className='mr-1 h-3.5 w-3.5' /> Predeterminar
                  </Button>
                )}
                <Button
                  variant='ghost'
                  size='icon-sm'
                  onClick={() => toggleActive(c)}
                  title={c.activa ? 'Desactivar' : 'Activar'}
                >
                  {c.activa ? (
                    <X className='h-3.5 w-3.5 text-muted-foreground' />
                  ) : (
                    <Check className='h-3.5 w-3.5 text-green-600' />
                  )}
                </Button>
                <Button variant='ghost' size='icon-sm' onClick={() => handleEdit(c)} title='Editar'>
                  <Pencil className='h-3.5 w-3.5' />
                </Button>
                <Button
                  variant='ghost'
                  size='icon-sm'
                  onClick={() => handleDelete(c)}
                  title={
                    c.reservasCount > 0
                      ? 'Tiene reservas asociadas: desactivala en lugar de eliminarla'
                      : 'Eliminar'
                  }
                >
                  <Trash2
                    className={cn(
                      'h-3.5 w-3.5',
                      c.reservasCount > 0 ? 'text-muted-foreground/40' : 'text-destructive'
                    )}
                  />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {activeCount === 1 && (
        <p className='text-xs text-muted-foreground'>
          Hay una sola cuenta activa: el checkout no muestra el selector y la asigna
          automáticamente.
        </p>
      )}
    </div>
  );
}
